import { useState, useEffect } from 'react';
import { Building2, Search, Plus, Edit, Trash2, Eye, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { CustomerForm } from './CustomerForm';
import { ViewCustomerModal } from './ViewCustomerModal';
import { useNavigate } from '../../hooks/useNavigate';

interface Customer {
  id: string;
  customer_number: string;
  name: string;
  mc_number?: string;
  billing_email?: string;
  billing_phone?: string;
  factoring_company?: string;
  is_locked: boolean;
  created_at: string;
}

export function CustomersView() {
  const toast = useToast();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [viewCustomerId, setViewCustomerId] = useState<string | null>(null);
  const [editCustomerId, setEditCustomerId] = useState<string | null>(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loadCount, setLoadCount] = useState(0);
  const [deleteOption, setDeleteOption] = useState<'delete-loads' | 'reassign' | null>(null);
  const [reassignCustomerId, setReassignCustomerId] = useState<string>('');

  useEffect(() => {
    loadCustomers();
  }, []);

  async function loadCustomers() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }

  async function checkLoadsAndDelete(customerId: string) {
    try {
      // Check how many loads this customer has
      const { count, error } = await supabase
        .from('loads')
        .select('*', { count: 'exact', head: true })
        .eq('customer_id', customerId);

      if (error) throw error;

      setLoadCount(count || 0);
      setDeleteCustomerId(customerId);
      
      // If no loads, can delete directly
      if (count === 0) {
        setDeleteOption('delete-loads'); // Will proceed to delete
      }
    } catch (error: any) {
      console.error('Error checking loads:', error);
      toast.error('Failed to check customer loads');
    }
  }

  async function handleDelete() {
    if (!deleteCustomerId) return;

    setDeleting(true);
    try {
      if (deleteOption === 'delete-loads') {
        // Delete all loads first, then delete customer
        const { error: loadsError } = await supabase
          .from('loads')
          .delete()
          .eq('customer_id', deleteCustomerId);

        if (loadsError) throw loadsError;
      } else if (deleteOption === 'reassign') {
        // Reassign loads to another customer
        if (!reassignCustomerId) {
          toast.error('Please select a customer to reassign loads to');
          setDeleting(false);
          return;
        }

        const { error: reassignError } = await supabase
          .from('loads')
          .update({ customer_id: reassignCustomerId })
          .eq('customer_id', deleteCustomerId);

        if (reassignError) throw reassignError;
      }

      // Now delete the customer
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', deleteCustomerId);

      if (error) throw error;
      
      toast.success('Customer deleted successfully');
      setDeleteCustomerId(null);
      setDeleteOption(null);
      setReassignCustomerId('');
      setLoadCount(0);
      loadCustomers();
    } catch (error: any) {
      console.error('Error deleting customer:', error);
      toast.error(error.message || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  }

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.customer_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.mc_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.billing_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              Customers
            </h1>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-sm text-slate-600">
                <Building2 className="w-4 h-4 inline mr-1" />
                <span className="font-bold">{filteredCustomers.length}</span> Total
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Actions */}
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">ID</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">MC/MX/FF Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Billing Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Billing Phone</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Factoring Company</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Locked</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {paginatedCustomers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                    No customers found
                  </td>
                </tr>
              ) : (
                paginatedCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-blue-600">{customer.customer_number}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-slate-900">{customer.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600">{customer.mc_number || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {customer.billing_email ? (
                        <span className="text-sm text-slate-900">{customer.billing_email}</span>
                      ) : (
                        <span className="text-sm text-slate-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600">{customer.billing_phone || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-slate-600">{customer.factoring_company || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {customer.is_locked && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                          Locked
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button 
                          onClick={() => setViewCustomerId(customer.id)}
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" 
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => navigate(`customers/edit/${customer.id}`)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => checkLoadsAndDelete(customer.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" 
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
            >
              <option value={5}>5 Per Page</option>
              <option value={10}>10 Per Page</option>
              <option value={25}>25 Per Page</option>
              <option value={50}>50 Per Page</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              «
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ‹
            </button>
            <span className="px-4 py-2 text-sm text-slate-700">
              Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ›
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              »
            </button>
          </div>
        </div>
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <CustomerForm
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadCustomers();
          }}
        />
      )}

      {/* View Customer Modal */}
      {viewCustomerId && (
        <ViewCustomerModal
          customerId={viewCustomerId}
          onClose={() => setViewCustomerId(null)}
          onEdit={() => {
            setViewCustomerId(null);
            navigate(`customers/edit/${viewCustomerId}`);
          }}
        />
      )}

      {/* Edit Customer Modal */}
      {editCustomerId && (
        <CustomerForm
          customerId={editCustomerId}
          onClose={() => setEditCustomerId(null)}
          onSuccess={() => {
            setEditCustomerId(null);
            loadCustomers();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteCustomerId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Delete Customer</h3>
                <p className="text-sm text-slate-600">
                  {loadCount === 0 ? 'This action cannot be undone' : `This customer has ${loadCount} load(s)`}
                </p>
              </div>
            </div>

            {loadCount === 0 ? (
              <p className="text-slate-700 mb-6">
                Are you sure you want to delete this customer? This action cannot be undone.
              </p>
            ) : (
              <div className="space-y-4 mb-6">
                <p className="text-slate-700">
                  This customer has <strong>{loadCount} load(s)</strong>. Choose how to proceed:
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors" style={{ borderColor: deleteOption === 'delete-loads' ? '#3b82f6' : '#e2e8f0' }}>
                    <input
                      type="radio"
                      name="deleteOption"
                      value="delete-loads"
                      checked={deleteOption === 'delete-loads'}
                      onChange={(e) => setDeleteOption(e.target.value as 'delete-loads')}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-slate-900">Delete all loads</div>
                      <div className="text-sm text-slate-600">Permanently delete all {loadCount} load(s) associated with this customer</div>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors" style={{ borderColor: deleteOption === 'reassign' ? '#3b82f6' : '#e2e8f0' }}>
                    <input
                      type="radio"
                      name="deleteOption"
                      value="reassign"
                      checked={deleteOption === 'reassign'}
                      onChange={(e) => setDeleteOption(e.target.value as 'reassign')}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">Reassign loads to another customer</div>
                      <div className="text-sm text-slate-600 mb-2">Transfer all loads to a different customer</div>
                      {deleteOption === 'reassign' && (
                        <select
                          value={reassignCustomerId}
                          onChange={(e) => setReassignCustomerId(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a customer...</option>
                          {customers
                            .filter(c => c.id !== deleteCustomerId)
                            .map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setDeleteCustomerId(null);
                  setDeleteOption(null);
                  setReassignCustomerId('');
                  setLoadCount(0);
                }}
                disabled={deleting}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting || (loadCount > 0 && !deleteOption) || (deleteOption === 'reassign' && !reassignCustomerId)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete Customer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

